import { useMemo } from 'react';
import { Select, SelectItem } from '@scholastic/mockingjay';
import { type MultiSelectOption } from 'eds-common';
import { LoadingSelect } from '@data/components/basic/LoadingSelect/LoadingSelect';
import { sortByLastName } from '@data/utils/sort-by-last-name';
import { useGetStudentsByClassAndEdAppApi } from '@data/hooks/api/use-get-students-by-class-and-ed-app-api/use-get-students-by-class-and-ed-app-api';
import type { ProductCode } from '@data/main';
import { LabeledMultiSelect } from '../../LabeledMultiSelect/LabeledMultiSelect';

interface StudentDropdownBaseProps {
  classId: string;
  edApp: ProductCode;
  /** Overrides the signed-in user's own entitlements; see the hook. */
  productCodes?: string[] | undefined;
}

type StudentDropdownProps =
  | (StudentDropdownBaseProps & {
      mode: 'multiselect';
      selectedStudentIds: string[];
      onChange: (studentIds: string[]) => void;
    })
  | (StudentDropdownBaseProps & {
      mode?: 'single';
      selectedStudentId: string;
      onChange: (studentId: string) => void;
    });

export function StudentDropdown({
  classId,
  edApp,
  productCodes,
  mode,
  ...rest
}: StudentDropdownProps) {
  const {
    data: students,
    isLoading,
    isError,
  } = useGetStudentsByClassAndEdAppApi(
    { classId, edApp, ...(productCodes ? { productCodes } : {}) },
    { retry: 0 }
  );

  const formattedStudents = useMemo(
    () =>
      [...(students ?? [])]
        .map((s) => ({
          id: s.id,
          firstName: s.given_name,
          lastName: s.family_name,
        }))
        .sort(sortByLastName),
    [students]
  );

  const studentOptions: MultiSelectOption[] = useMemo(
    () =>
      formattedStudents.map((s) => ({
        value: s.id,
        label: `${s.lastName}, ${s.firstName}`,
      })),
    [formattedStudents]
  );

  if (isLoading) return <LoadingSelect />;
  if (isError) throw new Error('Unable to fetch students');

  // ── Multiselect mode ─────────────────────────────────────────────────────────
  if (mode === 'multiselect') {
    const { selectedStudentIds, onChange: onMultiChange } = rest as {
      selectedStudentIds: string[];
      onChange: (studentIds: string[]) => void;
    };
    return (
      <LabeledMultiSelect
        label="Students"
        inputId="student-multiselect"
        selectAllLabel="All students"
        options={studentOptions}
        selectedIds={selectedStudentIds}
        onChange={onMultiChange}
      />
    );
  }

  // ── Single select mode ───────────────────────────────────────────────────────
  const { selectedStudentId, onChange: onSingleChange } = rest as {
    selectedStudentId: string;
    onChange: (studentId: string) => void;
  };

  return (
    <div className="flex w-full [&>div]:w-full [&_button]:min-w-0">
      <Select
        label="Students"
        value={selectedStudentId}
        onValueChange={onSingleChange}
      >
        {formattedStudents.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            {s.lastName}, {s.firstName}
          </SelectItem>
        ))}
      </Select>
    </div>
  );
}
